import { NgModule, ModuleWithProviders } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule } from '@angular/forms';

import { AiOnboardingConfig, AI_ONBOARDING_CONFIG, DEFAULT_AI_ONBOARDING_CONFIG } from './ai-onboarding.config';

// Import all standalone components
import {
  DashboardComponent,
  BillListComponent,
  BillComparisonComponent,
  HierarchyItemComponent,
  HierarchyAddressRowComponent,
  HierarchyMeterRowComponent,
  SmartInputComponent,
  UploadZoneComponent,
  DuplicateResolverComponent,
  PairedBillResolverComponent,
  StatsCardsComponent,
  ColumnSelectorComponent,
  CustomerReviewPanelComponent,
  SelectionActionBarComponent,
  MergeModalComponent,
  SortableHeaderComponent,
  MetricsBadgeComponent,
  MatchStatusBadgeComponent,
  BillTypeBadgeComponent,
  BillGroupCardComponent
} from './components';

// Import services
import { BillService } from './services/bill.service';
import { GeminiService } from './services/gemini.service';
import { MatchingService } from './services/matching.service';
import { GoogleMapsLoaderService } from './services/google/google-maps-loader.service';

/**
 * NgModule wrapper for the AI Onboarding module.
 *
 * This module is provided for compatibility with legacy NgModule-based applications.
 * For Angular 17+ applications using standalone components, prefer using
 * `provideAiOnboarding()` in your app.config.ts instead.
 *
 * Usage:
 * ```typescript
 * @NgModule({
 *   imports: [
 *     AiOnboardingModule.forRoot({
 *       googleMapsApiKey: 'YOUR_API_KEY', // optional
 *       defaultProMode: true // optional
 *     })
 *   ]
 * })
 * export class AppModule {}
 * ```
 *
 * Then use components in your templates:
 * ```html
 * <app-dashboard></app-dashboard>
 * ```
 */
@NgModule({
  imports: [
    CommonModule,
    HttpClientModule,
    FormsModule,
    // Import all standalone components so they can be used in templates
    DashboardComponent,
    BillListComponent,
    BillComparisonComponent,
    HierarchyItemComponent,
    HierarchyAddressRowComponent,
    HierarchyMeterRowComponent,
    SmartInputComponent,
    UploadZoneComponent,
    DuplicateResolverComponent,
    PairedBillResolverComponent,
    StatsCardsComponent,
    ColumnSelectorComponent,
    CustomerReviewPanelComponent,
    SelectionActionBarComponent,
    MergeModalComponent,
    SortableHeaderComponent,
    MetricsBadgeComponent,
    MatchStatusBadgeComponent,
    BillTypeBadgeComponent,
    BillGroupCardComponent
  ],
  exports: [
    // Export all components for use in consumer templates
    DashboardComponent,
    BillListComponent,
    BillComparisonComponent,
    HierarchyItemComponent,
    HierarchyAddressRowComponent,
    HierarchyMeterRowComponent,
    SmartInputComponent,
    UploadZoneComponent,
    DuplicateResolverComponent,
    PairedBillResolverComponent,
    StatsCardsComponent,
    ColumnSelectorComponent,
    CustomerReviewPanelComponent,
    SelectionActionBarComponent,
    MergeModalComponent,
    SortableHeaderComponent,
    MetricsBadgeComponent,
    MatchStatusBadgeComponent,
    BillTypeBadgeComponent,
    BillGroupCardComponent
  ]
})
export class AiOnboardingModule {
  /**
   * Use forRoot() when importing into your root AppModule.
   * This ensures services are singleton and configuration is available app-wide.
   *
   * @param config - Configuration for the AI Onboarding module
   */
  static forRoot(config?: Partial<AiOnboardingConfig>): ModuleWithProviders<AiOnboardingModule> {
    const finalConfig = { ...DEFAULT_AI_ONBOARDING_CONFIG, ...config };
    return {
      ngModule: AiOnboardingModule,
      providers: [
        { provide: AI_ONBOARDING_CONFIG, useValue: finalConfig },
        BillService,
        GeminiService,
        MatchingService,
        GoogleMapsLoaderService
      ]
    };
  }

  /**
   * Use forChild() when importing into feature modules.
   * This imports components without re-providing services.
   */
  static forChild(): ModuleWithProviders<AiOnboardingModule> {
    return {
      ngModule: AiOnboardingModule,
      providers: []
    };
  }
}
